import { useState, useRef, useEffect } from 'react'
import { ChevronDown } from 'lucide-react'

export interface SplitButtonMenuItem {
  label: string
  description?: string
  icon?: React.ReactNode
  onClick: () => void
}

export interface SplitButtonAdminIndicator {
  active: boolean
  tooltip?: string
}

interface SplitButtonProps {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  menuItems: SplitButtonMenuItem[]
  className?: string
  /**
   * Renders the chevron in a faded primary tone for admin-only
   * "feature is hidden from regular users" signalling. Only renders
   * when a chevron is rendered (menuItems.length > 0).
   */
  adminIndicator?: SplitButtonAdminIndicator
}

type MenuAlign = 'center' | 'right' | 'left'
const MENU_MIN_WIDTH = 260
const VIEWPORT_MARGIN = 8

export default function SplitButton({
  label,
  icon,
  onClick,
  menuItems,
  className = '',
  adminIndicator,
}: SplitButtonProps) {
  const [open, setOpen] = useState(false)
  const [menuAlign, setMenuAlign] = useState<MenuAlign>('center')
  const ref = useRef<HTMLDivElement>(null)

  const hasMenu = menuItems.length > 0
  const showIndicator = hasMenu && !!adminIndicator?.active

  function computeMenuAlign(): MenuAlign {
    if (!ref.current) return 'center'
    const rect = ref.current.getBoundingClientRect()
    const buttonCenter = (rect.left + rect.right) / 2
    const halfMenu = MENU_MIN_WIDTH / 2
    const vw = window.innerWidth
    if (buttonCenter + halfMenu > vw - VIEWPORT_MARGIN) return 'right'
    if (buttonCenter - halfMenu < VIEWPORT_MARGIN) return 'left'
    return 'center'
  }

  function toggleOpen() {
    if (open) {
      setOpen(false)
      return
    }
    // Pick alignment synchronously before opening so the menu paints in the
    // correct position on the first frame (avoids a center-to-right flash).
    setMenuAlign(computeMenuAlign())
    setOpen(true)
  }

  // Keep alignment correct when the user resizes the window with the menu open.
  useEffect(() => {
    if (!open) return
    function onResize() {
      setMenuAlign(computeMenuAlign())
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [open])

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  // Close the dropdown if the chevron disappears (e.g. flag flipped at runtime).
  useEffect(() => {
    if (!hasMenu && open) setOpen(false)
  }, [hasMenu, open])

  const primaryRounding = hasMenu ? '' : 'rounded-lg'

  return (
    <div ref={ref} className={`relative inline-flex ${className}`}>
      <div className={`inline-flex ${hasMenu ? 'rounded-lg overflow-hidden' : ''}`}>
        <button
          type="button"
          onClick={onClick}
          className={`flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors ${primaryRounding}`}
        >
          {icon}
          {label}
        </button>
        {hasMenu && (
          <button
            type="button"
            onClick={toggleOpen}
            title={showIndicator ? adminIndicator?.tooltip : undefined}
            className={`flex items-center px-2 py-2 transition-colors border-l ${
              showIndicator
                ? 'bg-primary/50 text-primary-foreground hover:bg-primary/60 border-primary-foreground/20'
                : 'bg-primary text-primary-foreground hover:bg-primary/80 border-primary-foreground/20'
            }`}
            aria-label="More options"
          >
            <ChevronDown className={`w-4 h-4 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>

      {open && hasMenu && (
        <div
          className={`absolute top-full mt-2 min-w-[260px] rounded-xl border border-border bg-card shadow-xl z-50 p-2 ${
            menuAlign === 'center'
              ? 'left-1/2 -translate-x-1/2'
              : menuAlign === 'right'
                ? 'right-0'
                : 'left-0'
          }`}
        >
          {menuItems.map((item, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setOpen(false)
                item.onClick()
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground hover:bg-primary/5 transition-colors text-left group"
            >
              {item.icon && (
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary shrink-0 group-hover:bg-primary/15 transition-colors">
                  {item.icon}
                </span>
              )}
              <span className="flex flex-col min-w-0">
                <span className="text-sm font-medium">{item.label}</span>
                {item.description && (
                  <span className="text-xs text-muted-foreground">{item.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
